import { useOrganizationCreate } from "@/api";
import NumerousLogo from "@/components/logo/NumerousLogo";
import { TypographyH4 } from "@/components/typography/H4";
import { TypographyMuted } from "@/components/typography/Muted";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { LoadingCircle } from "@/components/ui/loadingCircle";
import { toast } from "@/components/ui/use-toast";
import { Link, NavigateOptions, useRouter } from "@tanstack/react-router";
import { AnimatePresence, motion } from "framer-motion";
import { ArrowLeftCircle, Ban } from "lucide-react";
import { FormEvent, useState } from "react";
import { useLocalStorage } from "usehooks-ts";

interface Props {
  navigatePropsOnSuccess?: NavigateOptions;
}

export const OrganizationCreate = ({ navigatePropsOnSuccess }: Props) => {
  const [inputIsValid, setInputIsValid] = useState(true);
  const [organizationCreate, { loading, data, error }] =
    useOrganizationCreate();
  const { navigate } = useRouter();
  const [, setLatestOrganization] = useLocalStorage<string | null>(
    "latestOrganizationSlug",
    null
  );

  const typename = data?.organizationCreate.__typename;

  function handleSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();

    const formData = new FormData(e.currentTarget);
    const name = (formData.get("name") as string) || undefined;

    if (!name) return;

    const { hostname } = document.location;
    let whiteLabelDomain: string | undefined;
    if (!hostname.includes("localhost") && !hostname.includes("numerous"))
      whiteLabelDomain = hostname;

    organizationCreate({ variables: { name, whiteLabelDomain } })
      .then((res) => {
        const resultTypename = res.data?.organizationCreate.__typename;
        if (resultTypename === "Organization") {
          if (navigatePropsOnSuccess) navigate(navigatePropsOnSuccess);
          else if (res.data?.organizationCreate.slug) {
            setLatestOrganization(res.data.organizationCreate.slug);
            navigate({
              to: "/organization/$organizationSlug/gallery",
              params: { organizationSlug: res.data.organizationCreate.slug }
            });
          }
          toast({
            title: "Organization created",
            description: `You are now using the ${res.data?.organizationCreate.name} organization.`
          });
        } else if (resultTypename === "OrganizationSlugInvalid") {
          toast({
            title: "Invalid organization slug",
            description:
              "The chosen organization slug must contain only lowercase letters, numbers and dashes."
          });
        } else if (resultTypename === "OrganizationSlugOccupied") {
          toast({
            title: "Occupied organization slug",
            description:
              "The chosen organization slug is occupied. You have to choose a different one."
          });
        }
      })
      .catch((err) => {
        console.error(err);
      });
  }

  return (
    <div
      data-testid="organizationCreatePage"
      className="flex justify-center items-center w-full"
    >
      <div className="max-w-lg -mt-24">
        <AnimatePresence mode="wait" initial={false}>
          {!data && (
            <motion.form
              key="form"
              onSubmit={handleSubmit}
              className="relative"
              exit={{ top: "8px", opacity: 0 }}
            >
              <Link to="/organization">
                <Button
                  variant="outline"
                  size="sm"
                  className="place-self-start dark:text-brand-gray-300 hover:dark:text-brand-gray-100"
                >
                  <ArrowLeftCircle className="w-4 h-4 inline mr-2" />
                  Back
                </Button>
              </Link>
              <div className="text-brand-gray-800 dark:text-brand-gray-100 flex items-start font-semibold mb-2 gap-2 mt-24">
                <NumerousLogo type="brandmark" size="sm" />
                Organizations
              </div>
              <div className="text-brand-gray-700 dark:text-brand-gray-200 mb-6 text-xs leading-5">
                The organization feature is a structured space to keep the apps
                that you work on with team members. Organizations help to
                arrange your apps, manage who has access to them, and simplify
                the workflow with your team.
              </div>
              <Label htmlFor="name">Name</Label>
              <div className="mb-2 grid grid-cols-[1fr_auto_18px] items-center gap-4">
                <Input
                  type="text"
                  name="name"
                  id="name"
                  data-testid="organizationNameInput"
                  autoFocus
                  required
                  pattern={`^[0-9a-zA-Z \\-"':;]+$`}
                  onChange={(e) =>
                    setInputIsValid(
                      e.currentTarget.value.trim().length > 0 &&
                        e.currentTarget.checkValidity()
                    )
                  }
                  disabled={loading}
                />
                <Button
                  type="submit"
                  variant="outline"
                  disabled={loading || !inputIsValid}
                >
                  Create organization
                </Button>
                {loading && <LoadingCircle />}
              </div>
              <TypographyMuted>
                Organization name may only consist of{" "}
                <i className="dark:text-brand-gray-100">
                  <code>a-z</code>
                </i>
                ,{" "}
                <i className="dark:text-brand-gray-100">
                  <code>A-Z</code>
                </i>
                ,{" "}
                <i className="dark:text-brand-gray-100">
                  <code>0-9</code>
                </i>
                , <i className="dark:text-brand-gray-100">&quot; &quot;</i>, and
                the characters{" "}
                <i className="dark:text-brand-gray-100">
                  <code>&apos;;:&quot;</code>
                </i>
                .
              </TypographyMuted>
              {error && (
                <motion.div
                  className="relative flex items-center gap-2 mt-2 text-brand-error"
                  initial={{ bottom: "8px", opacity: 0 }}
                  animate={{ bottom: 0, opacity: 1 }}
                >
                  <Ban size={14} />
                  The organization name contains invalid characters. Please
                  choose another name.
                </motion.div>
              )}
            </motion.form>
          )}
          {data && typename === "Organization" && (
            <motion.div
              key="data"
              className="relative dark:text-brand-gray-50"
              initial={{ top: "8px", opacity: 0 }}
              animate={{ top: 0, opacity: 1 }}
            >
              <TypographyH4>Your organization was created!</TypographyH4>

              <div className="mt-6 mb-8 gap-y-1 grid grid-cols-[5rem_1fr]">
                <span className="dark:text-brand-gray-300">Name</span>
                {data.organizationCreate.name}
                <span className="dark:text-brand-gray-300">URL</span>
                https://platform.numerous.com/organization/
                {data.organizationCreate.slug}
              </div>
              <Link
                to="/organization/$organizationSlug/gallery"
                params={{ organizationSlug: data.organizationCreate.slug }}
              >
                <Button>
                  Go to organization - {data.organizationCreate.slug}
                </Button>
              </Link>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </div>
  );
};
